import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sunrise, Sparkles, RefreshCw, X, ExternalLink, Clock, Bot,
} from "lucide-react";

function timeAgo(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  const days = Math.floor(diff / 86400);
  if (days < 30) return `il y a ${days} j`;
  return `il y a ${Math.floor(days / 30)} mois`;
}

export default function MorningRoutineWidget() {
  const [suggestions, setSuggestions] = useState([]);
  const [lastRunAt, setLastRunAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [acceptingId, setAcceptingId] = useState(null);
  const [principals, setPrincipals] = useState([]);
  const [filterPrincipal, setFilterPrincipal] = useState("all");

  const load = async (principalFilter = filterPrincipal) => {
    setLoading(true);
    try {
      const params = {};
      if (principalFilter && principalFilter !== "all") {
        params.principal_id = principalFilter;
      }
      const [sRes, pRes] = await Promise.all([
        api.get("/morning-routine/suggestions", { params }),
        api.get("/companies", { params: { role: "PRINCIPAL", limit: 100 } }),
      ]);
      setSuggestions(sRes.data?.suggestions || []);
      setLastRunAt(sRes.data?.last_run_at || null);
      setPrincipals(pRes.data?.items || pRes.data || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Reload list when the Mandant filter changes
  useEffect(() => {
    load(filterPrincipal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterPrincipal]);

  const generate = async () => {
    setGenerating(true);
    try {
      const body = { max_gaps: 10 };
      if (filterPrincipal && filterPrincipal !== "all") {
        body.principal_ids = [filterPrincipal];
      }
      const { data } = await api.post("/morning-routine/generate", body);
      toast.success(`${data.suggestions_created || 0} suggestion(s) générée(s)`);
      await load(filterPrincipal);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setGenerating(false);
    }
  };

  const accept = async (suggestion, company) => {
    setAcceptingId(`${suggestion.id}-${company.name}`);
    try {
      await api.post("/morning-routine/accept", {
        suggestion_id: suggestion.id,
        company_name: company.name,
        company_website: company.website,
        title_buckets: ["decision_makers"],
        max_contacts: 50,
        enable_neverbounce: true,
        enable_tavily_backfill: true,
        enable_email_drafts: true,
      });
      toast.success(`🤖 Mission lancée pour ${company.name}`);
      await load(filterPrincipal);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setAcceptingId(null);
    }
  };

  const dismiss = async (suggestion) => {
    try {
      await api.post(`/morning-routine/dismiss/${suggestion.id}`);
      toast.success("Suggestion ignorée");
      await load(filterPrincipal);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const lastRunLabel = lastRunAt ? timeAgo(lastRunAt) : "jamais";
  const isFiltered = filterPrincipal && filterPrincipal !== "all";
  const filteredPrincipalName = isFiltered
    ? (principals.find((p) => p.id === filterPrincipal)?.name || "")
    : "";

  return (
    <Card data-testid="morning-routine-widget">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Sunrise className="h-4 w-4 text-amber-500" />
            <h3 className="font-semibold text-sm">Suggestions Morning Routine</h3>
            <Badge variant="outline" className="text-[10px]">
              <Clock className="h-2.5 w-2.5 mr-1" />
              Dernière génération : {lastRunLabel}
            </Badge>
            {suggestions.length > 0 && (
              <Badge variant="secondary" className="text-[10px]" data-testid="morning-routine-count">
                {suggestions.length} suggestion{suggestions.length > 1 ? "s" : ""}
                {isFiltered ? ` · ${filteredPrincipalName}` : ""}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Select value={filterPrincipal} onValueChange={setFilterPrincipal}>
              <SelectTrigger className="w-44 h-8 text-xs" data-testid="morning-routine-filter">
                <SelectValue placeholder="Tous Mandants" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous Mandants</SelectItem>
                {principals.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              onClick={generate}
              disabled={generating}
              data-testid="morning-routine-generate-btn"
            >
              {generating ? (
                <RefreshCw className="h-3.5 w-3.5 mr-2 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5 mr-2" />
              )}
              {generating ? "Génération…" : "🌅 Générer"}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Chargement…</div>
        ) : suggestions.length === 0 ? (
          <div className="py-8 text-center space-y-2">
            <p className="text-sm text-muted-foreground">
              {isFiltered
                ? `Aucune suggestion en attente pour ${filteredPrincipalName}. Cliquez sur "Générer" pour cibler ce Mandant uniquement.`
                : 'Aucune suggestion en attente. Cliquez sur "Générer des suggestions" pour identifier les meilleures opportunités à activer maintenant.'}
            </p>
            <p className="text-[11px] text-muted-foreground">
              Consommation : ~{isFiltered ? 5 : 10} appels Tavily, aucun Apollo (uniquement à la validation).
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="morning-routine-list">
            {suggestions.map((s) => (
              <div key={s.id} className="border border-border/60 rounded-lg p-3 space-y-2 bg-card">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px] border-rose-300 text-rose-700 bg-rose-50 dark:bg-rose-950/30">
                      {s.target
                        ? `Déficit : ${s.current_total}/${s.target}`
                        : `Gap : ${s.current_total} contacts`}
                    </Badge>
                    <span className="font-medium text-sm">
                      {s.principal_name} × {s.country_name} ({s.country_code})
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => dismiss(s)}
                    className="h-7 text-xs text-muted-foreground"
                  >
                    <X className="h-3 w-3 mr-1" /> Ignorer
                  </Button>
                </div>
                <div className="space-y-1.5">
                  {(s.suggestions || []).map((c, i) => {
                    const accepting = acceptingId === `${s.id}-${c.name}`;
                    return (
                      <div
                        key={i}
                        className="flex items-start gap-3 py-2 px-2 rounded border border-border/40 bg-muted/20 hover:bg-muted/40 transition"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-sm">{c.name}</p>
                            {c.website && (
                              <a
                                href={c.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[10px] text-emerald-700 hover:underline inline-flex items-center gap-0.5"
                              >
                                <ExternalLink className="h-2.5 w-2.5" /> Site
                              </a>
                            )}
                            {c.size_hint && (
                              <Badge variant="outline" className="text-[9px]">{c.size_hint}</Badge>
                            )}
                          </div>
                          <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">
                            {c.rationale}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => accept(s, c)}
                          disabled={accepting}
                          data-testid={`accept-${s.id}-${i}`}
                          className="h-8"
                        >
                          {accepting ? (
                            <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                          ) : (
                            <Bot className="h-3 w-3 mr-1" />
                          )}
                          {accepting ? "…" : "Lancer"}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="text-[10px] text-muted-foreground mt-3">
          Suggestions générées à la demande uniquement. Apollo et NeverBounce ne sont consommés
          qu'à partir du moment où vous validez "Lancer".
        </p>
      </CardContent>
    </Card>
  );
}
